import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";

export default function Login() {
  const { login } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [audience, setAudience] = useState("all"); // all | customer | team

  useEffect(() => {
    try {
      const r = localStorage.getItem("jero_last_role");
      if (r === "customer") setAudience("customer");
      else if (r === "admin" || r === "member") setAudience("team");
    } catch (e) { /* ignore */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const firstName = (u?.full_name || u?.email || "").split(" ")[0].split("@")[0];
      toast(`Welcome back${firstName ? `, ${firstName}` : ""}`, "success");
      nav("/");
    } catch (err) {
      toast(err?.response?.data?.detail || "Sign-in failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-stretch"
      style={{
        background:
          "linear-gradient(135deg, #0a4d29 0%, #0e6939 60%, #128043 100%)",
      }}
    >
      {/* Left brand panel (hidden on small screens) */}
      <div className="hidden md:flex flex-col justify-between flex-1 px-12 py-12 text-white">
        <div className="flex items-center gap-3">
          <img
            src="/brand/logo-transparent.png"
            alt="Jero-One"
            className="w-12 h-12 object-contain"
          />
          <div>
            <div className="font-bold text-lg leading-tight">Jero-One</div>
            <div className="text-white/70 text-xs uppercase tracking-wider">
              Workspace
            </div>
          </div>
        </div>

        <div className="max-w-md space-y-4" data-testid={`hero-${audience}`}>
          <h1 className="text-4xl font-bold tracking-tight leading-tight">
            {audience === "customer"
              ? "Your Jero-One support & document portal."
              : audience === "team"
              ? "Your procurement command centre."
              : "Your dedicated workspace with Jero-One."}
          </h1>
          <p className="text-white/75 text-[15px] leading-relaxed">
            {audience === "customer" && (
              <>
                Log a new support ticket, track SLA progress in real-time, and
                download branded quotations, job cards and delivery notes for
                every project — anytime, from anywhere.
              </>
            )}
            {audience === "team" && (
              <>
                Track every procurement pipeline (e-GP, UNGM, UNHCR, PAU-NSD),
                compile PPDA-format proposals, run the document vault and stay
                on top of expiring certificates — all in one place.
              </>
            )}
            {audience === "all" && (
              <>
                <strong className="text-white">Customers:</strong> log a support ticket,
                track SLA progress and pick up branded quotations, job cards and delivery
                notes — anytime.
                <br />
                <strong className="text-white">Jero-One team:</strong> manage every
                procurement pipeline (e-GP, UNGM, UNHCR, PAU-NSD), compile PPDA-format
                proposals and keep the document vault in one place.
              </>
            )}
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4">
            {(audience === "customer"
              ? [
                  ["Support Tickets", "Log · Track · Resolve"],
                  ["My Documents", "Quotations · Job Cards · SDNs"],
                  ["Real-time Updates", "Email + in-app alerts"],
                  ["SLA Timers", "Know when we'll respond"],
                ]
              : audience === "team"
              ? [
                  ["Bids & Pipelines", "e-GP · UNGM · UNHCR · NSD"],
                  ["AI Proposals", "PPDA-format, 1-click"],
                  ["Document Vault", "Certificates · CVs · Legal"],
                  ["Ticket Desk", "SLA · assignment · digests"],
                ]
              : [
                  ["Support Tickets", "Log issues · Track SLA"],
                  ["Quotations & SDNs", "Branded PDFs on demand"],
                  ["Bids & Pipelines", "e-GP · UNGM · UNHCR · NSD"],
                  ["AI Assistant", "PPDA proposals · quick answers"],
                ]
            ).map(([k, v]) => (
              <div key={k} className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-xs font-bold text-white/90">{k}</div>
                <div className="text-[11px] text-white/65 mt-0.5">{v}</div>
              </div>
            ))}
          </div>
          {audience !== "all" && (
            <button
              type="button"
              onClick={() => {
                try { localStorage.removeItem("jero_last_role"); } catch (e) { /* ignore */ }
                setAudience("all");
              }}
              className="text-[11px] text-white/50 hover:text-white/85 underline underline-offset-4 transition"
              data-testid="switch-audience"
            >
              Not you? See both views
            </button>
          )}
        </div>

        <div className="text-xs text-white/55">
          © {new Date().getFullYear()} Jero-One Technologies Ltd. · Uganda | USA | Canada
        </div>
      </div>

      {/* Right card panel */}
      <div className="flex-1 flex items-center justify-center bg-[#fafbfc] p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-2 mb-6">
            <img src="/brand/logo-transparent.png" alt="Jero-One" className="w-10 h-10 object-contain" />
            <div>
              <div className="font-bold text-base leading-tight">Jero-One</div>
              <div className="text-slate-500 text-[10px] uppercase tracking-wider">
                Workspace
              </div>
            </div>
          </div>

          <div className="card card-padded">
            <h1 className="h1 mb-1">Sign in</h1>
            <p className="muted text-sm mb-6">
              Welcome back — let&apos;s get to work.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label">Email address</label>
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@jero-onetechnologies.com"
                />
              </div>
              <div>
                <label className="label">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                />
              </div>
              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="btn btn-primary btn-lg w-full justify-center"
              >
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] uppercase tracking-wider muted">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <button
              type="button"
              data-testid="google-signin-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="btn btn-ghost btn-lg w-full justify-center border border-slate-200"
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </button>
            <p className="text-[11px] muted text-center mt-2">
              Works for invited accounts only — your Google email must match your workspace account.
            </p>

            <div className="flex items-center justify-between text-xs mt-4">
              <Link
                to="/forgot-password"
                data-testid="goto-forgot"
                className="muted hover:text-[#0e6939] transition"
              >
                Forgot password?
              </Link>
            </div>

            <div className="text-center text-xs muted mt-5">
              Don&apos;t have an account?{" "}
              <Link
                to="/register"
                data-testid="goto-register"
                className="font-semibold"
                style={{ color: "var(--green-700)" }}
              >
                Create one
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
